/**
 * Agent dashboard FlightsHotel LDS orders → PaymentAgent (/p/dashboard/payment/:uuid).
 * Public / customer / organized-tour flows keep legacy /packages/profile navigation.
 */
import { resolvePaymentOrderType } from "./resolvePaymentOrderType.js";
import {
  ordersDashboardCategoryPath,
} from "../ordersDashboardRoutes.js";

const FLIGHTS_HOTEL_DEAL_TYPES = new Set(["FlightsHotel"]);

export const AGENT_LDS_FLIGHTSHOTEL_CHECKOUT_SOURCE = "agent_flightshotel_checkout";
/** Agent unsaved package profile — specific hotel/deal checkout (not /packages/profile/:uuid). */
export const AGENT_LDS_FLIGHTSHOTEL_PACKAGE_PROFILE_RETURN_TO = "/packages/profile";
export const AGENT_LDS_FLIGHTSHOTEL_DEFAULT_RETURN_TO =
  AGENT_LDS_FLIGHTSHOTEL_PACKAGE_PROFILE_RETURN_TO;

/**
 * @param {string|null|undefined} path
 */
export function isAgentUnsavedPackageProfileReturnPath(path) {
  const p = String(path || "").trim().split("?")[0];
  return p === AGENT_LDS_FLIGHTSHOTEL_PACKAGE_PROFILE_RETURN_TO;
}

/**
 * Dashboard + unsaved /packages/profile back targets (never /packages/profile/:uuid).
 * @param {string|null|undefined} path
 */
export function isAgentLdsFlightsHotelSafeReturnPath(path) {
  const p = String(path || "").trim().split("?")[0];
  if (isAgentUnsavedPackageProfileReturnPath(p)) return true;
  if (/^\/(he|en)\/campaigns\//.test(p)) return true;
  if (!p.startsWith("/p/dashboard")) return false;
  if (p.startsWith("/p/dashboard/payment/")) return false;
  if (p.includes("/packages/profile")) return false;
  return true;
}

/**
 * Resolve returnTo when navigating from agent checkout → PaymentAgent.
 * Maps /packages/profile/* (incl. saved uuid) → unsaved /packages/profile (deal in context).
 * Never returns /packages/profile/:uuid.
 * @param {{ pathname?: string, search?: string }} [ctx]
 */
export function resolveAgentLdsFlightsHotelCheckoutReturnTo(ctx = {}) {
  const path = String(ctx.pathname || "").split("?")[0];
  const searchRaw = ctx.search == null ? "" : String(ctx.search);
  const search =
    searchRaw && !searchRaw.startsWith("?") ? `?${searchRaw}` : searchRaw;
  if (
    /^\/packages\/profile(\/draft\/[^/]+|\/[^/]+)?\/?$/.test(path) &&
    !/\/success\/?$/.test(path)
  ) {
    return AGENT_LDS_FLIGHTSHOTEL_PACKAGE_PROFILE_RETURN_TO;
  }
  if (isAgentLdsFlightsHotelSafeReturnPath(path)) {
    return `${path}${search}`;
  }
  return AGENT_LDS_FLIGHTSHOTEL_DEFAULT_RETURN_TO;
}

/**
 * Logged-in agent must not use /packages/profile/:uuid as a post-order work page.
 * @param {{
 *   isAgentLoggedIn?: boolean,
 *   isPublicOffer?: boolean,
 *   isCustomerLinkMode?: boolean,
 *   pathname?: string,
 * }} [ctx]
 */
export function shouldBlockAgentSavedOrderProfileWorkSession(ctx = {}) {
  const {
    isAgentLoggedIn = false,
    isPublicOffer = false,
    isCustomerLinkMode = false,
    pathname = "",
  } = ctx;
  if (!isAgentLoggedIn) return false;
  if (isPublicOffer || isCustomerLinkMode) return false;
  const p = String(pathname || "").split("?")[0];
  if (/^\/packages\/profile\/draft\//.test(p)) return false;
  if (!/^\/packages\/profile\/[^/]+\/?$/.test(p)) return false;
  if (/\/success\/?$/.test(p)) return false;
  return true;
}

/**
 * Router state passed when agent checkout navigates to PaymentAgent.
 * @param {{ returnTo?: string, orderId?: string|null, pathname?: string, search?: string }} [options]
 */
export function buildAgentLdsFlightsHotelPaymentNavState(options = {}) {
  const { returnTo: returnToOpt, orderId = null, pathname = "", search = "" } = options;
  const candidate =
    returnToOpt ??
    resolveAgentLdsFlightsHotelCheckoutReturnTo({ pathname, search });
  const trimmed = String(candidate || "").trim();
  const pathOnly = trimmed.split("?")[0];
  const safeReturnTo = isAgentLdsFlightsHotelSafeReturnPath(pathOnly)
    ? trimmed.split("#")[0]
    : AGENT_LDS_FLIGHTSHOTEL_DEFAULT_RETURN_TO;
  const oid = orderId == null ? "" : String(orderId).trim();
  return {
    source: AGENT_LDS_FLIGHTSHOTEL_CHECKOUT_SOURCE,
    returnTo: safeReturnTo,
    ...(oid ? { orderId: oid } : {}),
  };
}

/**
 * Redirect target when agent hits saved order profile (legacy customer URL).
 * @param {string|null|undefined} orderId
 * @param {object|null|undefined} orderRow
 * @param {Parameters<typeof shouldUseAgentDashboardLdsPaymentNav>[0]} ctx
 */
export function resolveAgentBlockedSavedProfileRedirect(orderId, orderRow, ctx = {}) {
  const paymentPath = resolveAgentLdsFlightsHotelPostOrderPath(orderId, orderRow, {
    ...ctx,
    isAgentLoggedIn: true,
  });
  const returnTo = resolveAgentLdsFlightsHotelCheckoutReturnTo({
    pathname: ctx.pathname,
    search: ctx.search,
  });
  if (paymentPath) {
    return {
      path: paymentPath,
      state: buildAgentLdsFlightsHotelPaymentNavState({
        returnTo,
        orderId,
        pathname: ctx.pathname,
      }),
    };
  }
  return { path: AGENT_LDS_FLIGHTSHOTEL_DEFAULT_RETURN_TO, state: null };
}

/**
 * Back target for agent LDS payment page — dashboard paths only.
 * @param {object|null|undefined} locationState
 * @param {{ cameFromOrders?: boolean }} [options]
 */
export function resolveAgentDashboardPaymentReturnTo(locationState, options = {}) {
  const { cameFromOrders = false } = options;
  if (cameFromOrders) {
    return ordersDashboardCategoryPath("packages");
  }
  const returnTo = locationState?.returnTo;
  if (typeof returnTo === "string") {
    const trimmed = returnTo.trim();
    const path = trimmed.split("?")[0];
    if (isAgentLdsFlightsHotelSafeReturnPath(path)) {
      return trimmed.split("#")[0];
    }
  }
  return AGENT_LDS_FLIGHTSHOTEL_DEFAULT_RETURN_TO;
}

/**
 * Safe navigate target for agent LDS PaymentAgent back button (never /packages/profile/*).
 * @param {object|null|undefined} locationState
 * @param {Parameters<typeof resolveAgentDashboardPaymentReturnTo>[1]} [options]
 */
export function resolveAgentLdsFlightsHotelPaymentBackTarget(
  locationState,
  options = {}
) {
  return resolveAgentDashboardPaymentReturnTo(locationState, options);
}

/**
 * @param {object|null|undefined} orderRow
 */
export function isLdsFlightsHotelDealType(orderRow) {
  const dt = String(
    orderRow?.dealtype ?? orderRow?.dealType ?? orderRow?.deal_type ?? "FlightsHotel"
  ).trim();
  return FLIGHTS_HOTEL_DEAL_TYPES.has(dt);
}

/**
 * @param {string|null|undefined} orderId
 * @param {object|null|undefined} [orderRow]
 * @returns {string|null}
 */
export function getAgentDashboardLdsPaymentPath(orderId, orderRow = null) {
  const id = orderId == null ? "" : String(orderId).trim();
  if (!id || !resolvePaymentOrderType(id).isUuidOrder) return null;
  if (orderRow != null && !isLdsFlightsHotelDealType(orderRow)) return null;
  return `/p/dashboard/payment/${encodeURIComponent(id)}`;
}

/**
 * @param {{
 *   pathname?: string,
 *   inlinePackageStoryFlow?: boolean,
 *   hasPublicBookingSuccessHandler?: boolean,
 *   isCustomerLinkMode?: boolean,
 *   isCustomerBookingMode?: boolean,
 *   isPublicOffer?: boolean,
 *   customerQuoteLinkOnly?: boolean,
 *   isAgentLoggedIn?: boolean,
 * }} [ctx]
 */
export function shouldUseAgentDashboardLdsPaymentNav(ctx = {}) {
  const {
    pathname = "",
    inlinePackageStoryFlow = false,
    hasPublicBookingSuccessHandler = false,
    isCustomerLinkMode = false,
    isCustomerBookingMode = false,
    isPublicOffer = false,
    customerQuoteLinkOnly = false,
    isAgentLoggedIn = false,
  } = ctx;

  if (inlinePackageStoryFlow || hasPublicBookingSuccessHandler) return false;
  if (isCustomerLinkMode || isCustomerBookingMode || isPublicOffer || customerQuoteLinkOnly) {
    return false;
  }

  const path = String(pathname || "").split("?")[0];
  if (path.startsWith("/p/dashboard")) return true;

  // Agent package checkout on /packages/profile (unsaved, draft, or saved order uuid)
  if (isAgentLoggedIn && /^\/packages\/profile(\/draft\/[^/]+|\/[^/]+)?\/?$/.test(path)) {
    if (/\/success\/?$/.test(path)) return false;
    return true;
  }

  return false;
}

/**
 * @param {string|null|undefined} orderId
 * @param {object|null|undefined} orderRow
 * @param {Parameters<typeof shouldUseAgentDashboardLdsPaymentNav>[0]} ctx
 * @returns {string|null} PaymentAgent path or null (caller keeps legacy profile path)
 */
export function resolveAgentLdsFlightsHotelPostOrderPath(orderId, orderRow, ctx) {
  if (!shouldUseAgentDashboardLdsPaymentNav(ctx)) return null;
  return getAgentDashboardLdsPaymentPath(orderId, orderRow);
}
